import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

const LANGS = [
  { code: 'en', label: 'EN', flag: '/flags/gb.svg' },
  { code: 'se', label: 'SE', flag: '/flags/se.svg' },
  { code: 'dk', label: 'DK', flag: '/flags/dk.svg' },
  { code: 'no', label: 'NO', flag: '/flags/no.svg' },
  { code: 'fi', label: 'FI', flag: '/flags/fi.svg' },
  { code: 'is', label: 'IS', flag: '/flags/is.svg' },
];

export default function LanguageSwitcher({ compact = true, usePortal = false, placement = 'bottom' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(LANGS.find(l => l.code === (i18n.language || 'en')) || LANGS[0]);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  const selectLang = (lang) => {
    i18n.changeLanguage(lang.code);
    localStorage.setItem('i18nextLng', lang.code);
    setCurrent(lang);
    setOpen(false);
  };

  const toggleOpen = () => {
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open || !usePortal) return;
    const el = btnRef.current;
    if (!el) return;
    const MENU_WIDTH = 144; // w-36
    const PADDING = 8;

    const calc = () => {
      const rect = el.getBoundingClientRect();
      let left = rect.right - MENU_WIDTH;
      if (left < PADDING) left = PADDING;
      let top;
      if (placement === 'top') {
        const h = document.getElementById('lang-menu-popup')?.offsetHeight ?? 200;
        top = rect.top - PADDING - h;
      } else {
        top = rect.bottom + PADDING;
      }
      setPos({ left, top });
    };

    calc();
    const handle = () => calc();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open, usePortal, placement]);

  useEffect(() => {
    const code = i18n.language;
    const match = LANGS.find(l => l.code === code);
    if (match) setCurrent(match);
  }, [i18n.language]);

  const menu = (
    <div id="lang-menu-popup" className={usePortal ? 'w-36 card z-[1000]' : 'absolute right-0 mt-2 w-36 card z-50'}
      style={usePortal && pos ? { position: 'fixed', left: pos.left, top: pos.top } : undefined}
    >
      <ul className="text-sm">
        {LANGS.map((l) => (
          <li key={l.code}>
            <button
              type="button"
              onClick={() => selectLang(l)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
            >
              <img src={l.flag} alt={`${l.label} flag`} className="w-5 h-5" />
              <span>{l.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        className={compact ? 'icon-btn w-10 h-10' : 'btn btn-outline w-10 h-10'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Language selector"
      >
        <img src={current.flag} alt={`Current language: ${current.label}`} className="w-5 h-5" />
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {open && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}
